const EVENT_DATE_TIME_REGEX = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Convierte un valor de fecha a un objeto Date local.
 *
 * @param {Date|string|number} value - Valor a convertir.
 * @returns {Date|null} Fecha válida o null si no se puede interpretar.
 */
export const parseEventDateTime = (value) => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
    }

    if (typeof value === "string") {
        const trimmedValue = value.trim();
        const matchedDateTime = trimmedValue.match(EVENT_DATE_TIME_REGEX);

        if (matchedDateTime) {
            const [, year, month, day, hours, minutes, seconds = "00"] = matchedDateTime;
            const parsedDate = new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hours),
                Number(minutes),
                Number(seconds),
            );

            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        const parsedDate = new Date(trimmedValue);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "number") {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
};

/**
 * Formatea una fecha al patrón persistido por eventos.
 *
 * @param {Date|string|number} value - Fecha origen.
 * @returns {string|null} Fecha normalizada `YYYY-MM-DD HH:mm:ss` o null si es inválida.
 */
export const formatEventDateTime = (value) => {
    const parsedDate = parseEventDateTime(value);

    if (!parsedDate) {
        return null;
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const seconds = String(parsedDate.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Formatea una fecha al patrón local `YYYY-MM-DD`.
 *
 * @param {Date|string|number} value - Fecha origen.
 * @returns {string|null} Fecha normalizada o null si es inválida.
 */
export const formatEventDateOnly = (value) => {
    const parsedDate = parseEventDateTime(value);

    if (!parsedDate) {
        return null;
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

/**
 * Desplaza una fecha local por días y devuelve `YYYY-MM-DD`.
 *
 * @param {Date|string|number} value - Fecha origen.
 * @param {number} days - Cantidad de días a mover.
 * @returns {string|null} Fecha desplazada o null si es inválida.
 */
export const shiftEventDateOnly = (value, days) => {
    const parsedDate = parseEventDateTime(value);

    if (!parsedDate || !Number.isInteger(days)) {
        return null;
    }

    const shiftedDate = new Date(parsedDate.getTime());
    shiftedDate.setDate(shiftedDate.getDate() + days);

    return formatEventDateOnly(shiftedDate);
};

/**
 * Valida si una fecha puede persistirse de forma segura.
 *
 * @param {Date|string|number} value - Fecha origen.
 * @returns {boolean} True si puede persistirse.
 */
export const isValidEventDateTime = (value) => Boolean(formatEventDateTime(value));

/**
 * Normaliza fechas de un evento antes de enviarlo al backend.
 *
 * @param {Object} event - Evento origen.
 * @returns {Object|null} Evento con `start` y `end` normalizados o null si alguna fecha es inválida.
 */
export const normalizeEventDateRange = (event) => {
    if (!event) {
        return null;
    }

    const normalizedStart = formatEventDateTime(event.start);
    const normalizedEnd = formatEventDateTime(event.end);

    if (!normalizedStart || !normalizedEnd) {
        return null;
    }

    return {
        ...event,
        start: normalizedStart,
        end: normalizedEnd,
    };
};
